import { CONFIG_ELEMENTS } from '@vedalib/editor/lib/elements'
import lodash from 'lodash'
import { useCallback, useMemo } from 'react'

import Icon from 'components/uiKit/Icon'
import { KitSize } from 'components/uiKit/KitTypes'
import { PreviewMode } from 'services/Store/Project/enums'
import { useProjectContext } from 'services/Store/Project/hooks'
import { getBlockId } from 'services/Store/Project/selectors'
import { useGetEntityAnalyticsConfig } from 'services/lms/hooks/useGetEntityAnalyticsConfig'

import { useSimulatorContext } from '../SimulatorContext'
import NavigationButtonStyled from '../styled/NavigationButtonStyled'
import { NavigationRootStyled, NavigationButtonsStyled } from '../styled/NavigationStyled'

interface INavigationProps {
  viewed: number
}

const Navigation = ({ viewed }: INavigationProps) => {
  const setState = useSimulatorContext((c) => c.setState)
  const state = useSimulatorContext((c) => c.state)
  const value = useSimulatorContext((c) => c.value)
  const styles = useSimulatorContext((c) => c.styles)
  const font = useSimulatorContext((c) => c.font)
  const elementId = useSimulatorContext((c) => c.elementId) || ''
  const mode = useSimulatorContext((c) => c.mode)
  const validateFields = useSimulatorContext((c) => c.validateFields)
  const blockId = useProjectContext(getBlockId) || ''
  const isEditor = mode.previewMode === PreviewMode.editor
  const items = useMemo(() => value.items || [], [value.items])
  const activeItemIndex = Math.max(lodash.findIndex(items, { value: state?.active || '' }), 0)
  const { entityAnalyticsConfig, sendXapiData, baseObjectExtensions } = useGetEntityAnalyticsConfig(
    {
      blockId,
      elementType: CONFIG_ELEMENTS.simulator.type,
      elementId,
    },
  )

  const nextItem = useCallback(() => {
    if (!isEditor && !validateFields()) {
      return
    }

    const nextIndex = (activeItemIndex + 1) % items.length
    const active = items[nextIndex].value
    setState?.({ active })
    if (!elementId) {
      return
    }
    const data = entityAnalyticsConfig.interacted({
      resultExtensions: {
        toSlideId: active,
        toSlideIndex: nextIndex,
        fromSlideId: items[activeItemIndex]?.value,
        fromSlideIndex: activeItemIndex,
      },
      objectExtensions: { ...baseObjectExtensions, itemId: active },
      elementId,
    })
    sendXapiData(data)
  }, [
    activeItemIndex,
    entityAnalyticsConfig,
    baseObjectExtensions,
    elementId,
    isEditor,
    items,
    sendXapiData,
    setState,
    validateFields,
  ])

  const prevItem = () => {
    if (!isEditor && !validateFields()) {
      return
    }

    const prevIndex = (activeItemIndex - 1 + items.length) % items.length
    setState?.({ active: items[prevIndex].value })
    if (!elementId) {
      return
    }
    const data = entityAnalyticsConfig.interacted({
      resultExtensions: {
        toSlideId: items[prevIndex].value,
        toSlideIndex: prevIndex,
        fromSlideId: items[activeItemIndex]?.value,
        fromSlideIndex: activeItemIndex,
      },
      objectExtensions: baseObjectExtensions,
      elementId,
    })
    sendXapiData(data)
  }

  return (
    <NavigationRootStyled $styles={styles} $value={value}>
      {value.numbering && (
        <div style={font.navigation}>
          <span>
            {viewed}/{items.length}
          </span>
        </div>
      )}
      <NavigationButtonsStyled $styles={styles}>
        <NavigationButtonStyled
          $position='left'
          $styles={styles}
          aria-label='Previous item'
          onClick={prevItem}
        >
          {!styles.backButton?.backgroundImage && (
            <Icon name='builderInputBack' size={KitSize.S} aria-hidden />
          )}
        </NavigationButtonStyled>
        <NavigationButtonStyled
          $position='right'
          $styles={styles}
          aria-label='Next item'
          onClick={nextItem}
        >
          {!styles.nextButton?.backgroundImage && (
            <Icon name='builderInputForward' size={KitSize.S} aria-hidden />
          )}
        </NavigationButtonStyled>
      </NavigationButtonsStyled>
    </NavigationRootStyled>
  )
}

export default Navigation
